import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, FileTextIcon, PaperclipIcon, CheckCircleIcon, XIcon } from 'lucide-react';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Card from '../components/ui/Card';
const ApplicationFlow: React.FC = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [progress, setProgress] = useState(25);
  // Mock job data - in a real app, you would fetch this based on the ID
  const job = {
    id,
    title: 'Senior React Developer',
    company: 'SpaceX',
    logo: 'https://logo.clearbit.com/spacex.com'
  };
  const handleBack = () => {
    if (currentStep === 1) {
      navigate(-1);
    } else {
      setCurrentStep(currentStep - 1);
      setProgress(progress - 25);
    }
  };
  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
      setProgress(progress + 25);
    } else {
      navigate('/jobs');
      // In a real app, you would submit the application here
    }
  };
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <PersonalInfoStep data-id="element-58" />;
      case 2:
        return <ResumeStep data-id="element-59" />;
      case 3:
        return <CoverLetterStep data-id="element-60" />;
      case 4:
        return <ReviewStep data-id="element-61" />;
      default:
        return null;
    }
  };
  const PersonalInfoStep = () => <div data-id="element-62">
      <h2 className="text-xl font-semibold mb-4" data-id="element-63">Personal Information</h2>
      <div className="space-y-4" data-id="element-64">
        <Input label="Full Name" placeholder="John Doe" required fullWidth data-id="element-65" />
        <Input label="Email" type="email" placeholder="john@example.com" required fullWidth data-id="element-66" />
        <Input label="Phone Number" type="tel" placeholder="+1 (123) 456-7890" required fullWidth data-id="element-67" />
        <Input label="Current Location" placeholder="City, Country" fullWidth data-id="element-68" />
        <div data-id="element-69">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-70">
            LinkedIn Profile (Optional)
          </label>
          <Input placeholder="https://linkedin.com/in/username" fullWidth data-id="element-71" />
        </div>
      </div>
    </div>;
  const ResumeStep = () => <div data-id="element-72">
      <h2 className="text-xl font-semibold mb-4" data-id="element-73">Resume / CV</h2>
      <Card className="mb-6" data-id="element-74">
        <div className="flex items-center justify-between" data-id="element-75">
          <div className="flex items-center" data-id="element-76">
            <FileTextIcon size={24} className="text-teal-500 mr-3" data-id="element-77" />
            <div data-id="element-78">
              <p className="font-medium" data-id="element-79">Upload your resume</p>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-80">
                PDF, DOCX or RTF (Max 5MB)
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" data-id="element-81">
            Browse
          </Button>
        </div>
      </Card>
      <div className="mb-6" data-id="element-82">
        <h3 className="text-lg font-medium mb-3" data-id="element-83">
          Or select from saved resumes
        </h3>
        <div className="space-y-3" data-id="element-84">
          <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center justify-between bg-gray-50 dark:bg-gray-800" data-id="element-85">
            <div className="flex items-center" data-id="element-86">
              <input type="radio" name="resume" id="resume1" className="mr-3" data-id="element-87" />
              <div data-id="element-88">
                <label htmlFor="resume1" className="font-medium cursor-pointer" data-id="element-89">
                  John_Doe_Resume_2023.pdf
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-90">
                  Updated 2 months ago
                </p>
              </div>
            </div>
            <Button variant="ghost" size="sm" data-id="element-91">
              <PaperclipIcon size={16} data-id="element-92" />
            </Button>
          </div>
          <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center justify-between" data-id="element-93">
            <div className="flex items-center" data-id="element-94">
              <input type="radio" name="resume" id="resume2" className="mr-3" data-id="element-95" />
              <div data-id="element-96">
                <label htmlFor="resume2" className="font-medium cursor-pointer" data-id="element-97">
                  John_Doe_Resume_Tech.pdf
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-98">
                  Updated 5 months ago
                </p>
              </div>
            </div>
            <Button variant="ghost" size="sm" data-id="element-99">
              <PaperclipIcon size={16} data-id="element-100" />
            </Button>
          </div>
        </div>
      </div>
    </div>;
  const CoverLetterStep = () => <div data-id="element-101">
      <h2 className="text-xl font-semibold mb-4" data-id="element-102">Cover Letter</h2>
      <div className="mb-4" data-id="element-103">
        <div className="flex items-center justify-between mb-2" data-id="element-104">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-105">
            Cover Letter (Optional)
          </label>
          <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-106">
            0/500 words
          </span>
        </div>
        <textarea className="w-full h-64 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 p-3 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400" placeholder="Explain why you're a good fit for this position..." data-id="element-107"></textarea>
      </div>
      <div className="mb-6" data-id="element-108">
        <h3 className="text-lg font-medium mb-3" data-id="element-109">Or upload a cover letter</h3>
        <Card data-id="element-110">
          <div className="flex items-center justify-between" data-id="element-111">
            <div className="flex items-center" data-id="element-112">
              <FileTextIcon size={24} className="text-teal-500 mr-3" data-id="element-113" />
              <div data-id="element-114">
                <p className="font-medium" data-id="element-115">Upload cover letter</p>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-116">
                  PDF, DOCX or RTF (Max 2MB)
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" data-id="element-117">
              Browse
            </Button>
          </div>
        </Card>
      </div>
    </div>;
  const ReviewStep = () => <div data-id="element-118">
      <h2 className="text-xl font-semibold mb-4" data-id="element-119">Review Your Application</h2>
      <Card className="mb-4" data-id="element-120">
        <h3 className="font-medium mb-2" data-id="element-121">Personal Information</h3>
        <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-122">
          <div data-id="element-123">
            <p className="text-gray-500 dark:text-gray-400" data-id="element-124">Full Name</p>
            <p data-id="element-125">John Doe</p>
          </div>
          <div data-id="element-126">
            <p className="text-gray-500 dark:text-gray-400" data-id="element-127">Email</p>
            <p data-id="element-128">john@example.com</p>
          </div>
          <div data-id="element-129">
            <p className="text-gray-500 dark:text-gray-400" data-id="element-130">Phone</p>
            <p data-id="element-131">+1 (123) 456-7890</p>
          </div>
          <div data-id="element-132">
            <p className="text-gray-500 dark:text-gray-400" data-id="element-133">Location</p>
            <p data-id="element-134">New York, USA</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" className="mt-2" data-id="element-135">
          Edit
        </Button>
      </Card>
      <Card className="mb-4" data-id="element-136">
        <h3 className="font-medium mb-2" data-id="element-137">Resume</h3>
        <div className="flex items-center" data-id="element-138">
          <FileTextIcon size={18} className="text-teal-500 mr-2" data-id="element-139" />
          <p data-id="element-140">John_Doe_Resume_2023.pdf</p>
        </div>
        <Button variant="ghost" size="sm" className="mt-2" data-id="element-141">
          Change
        </Button>
      </Card>
      <Card className="mb-6" data-id="element-142">
        <h3 className="font-medium mb-2" data-id="element-143">Cover Letter</h3>
        <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-3" data-id="element-144">
          Dear Hiring Manager, I am writing to express my interest in the Senior
          React Developer position at SpaceX. With over 5 years of experience in
          building complex React applications...
        </p>
        <Button variant="ghost" size="sm" className="mt-2" data-id="element-145">
          Edit
        </Button>
      </Card>
      <div className="mb-4" data-id="element-146">
        <label className="flex items-start" data-id="element-147">
          <input type="checkbox" className="mt-1 mr-2" data-id="element-148" />
          <span className="text-sm" data-id="element-149">
            I confirm that all the information provided is accurate and complete
            to the best of my knowledge.
          </span>
        </label>
      </div>
    </div>;
  return <div className="pb-28" data-id="element-150">
      <div className="bg-gradient-to-r from-teal-500 to-cyan-400 p-4 flex items-center" data-id="element-151">
        <button onClick={handleBack} className="p-2 rounded-full bg-white/20 text-white mr-4" data-id="element-152">
          <ArrowLeftIcon size={20} data-id="element-153" />
        </button>
        <div data-id="element-154">
          <h1 className="text-lg font-bold text-white" data-id="element-155">Apply for Job</h1>
          <p className="text-sm text-white/80" data-id="element-156">
            {job.title} at {job.company}
          </p>
        </div>
      </div>
      <div className="p-4" data-id="element-157">
        {/* Progress bar */}
        <div className="mb-6" data-id="element-158">
          <div className="flex justify-between mb-2" data-id="element-159">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-160">
              Step {currentStep} of 4
            </span>
            <span className="text-sm font-medium text-teal-500 dark:text-teal-400" data-id="element-161">
              {progress}%
            </span>
          </div>
          <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-162">
            <div className="h-full bg-gradient-to-r from-teal-500 to-cyan-400 transition-all duration-300" style={{
            width: `${progress}%`
          }} data-id="element-163"></div>
          </div>
        </div>
        {/* Steps indicator */}
        <div className="flex justify-between mb-8" data-id="element-164">
          {[1, 2, 3, 4].map(step => <div key={step} className={`flex flex-col items-center ${step <= currentStep ? 'text-teal-500 dark:text-teal-400' : 'text-gray-400 dark:text-gray-500'}`} data-id="element-165">
              <div className={`
                w-8 h-8 rounded-full flex items-center justify-center mb-1
                ${step < currentStep ? 'bg-teal-500 text-white' : step === currentStep ? 'border-2 border-teal-500 dark:border-teal-400' : 'border-2 border-gray-300 dark:border-gray-600'}
              `} data-id="element-166">
                {step < currentStep ? <CheckCircleIcon size={16} data-id="element-167" /> : <span className="text-xs" data-id="element-168">{step}</span>}
              </div>
              <span className="text-xs" data-id="element-169">
                {step === 1 ? 'Info' : step === 2 ? 'Resume' : step === 3 ? 'Cover' : 'Review'}
              </span>
            </div>)}
        </div>
        {/* Step content */}
        <div data-id="element-170">{renderStepContent()}</div>
      </div>
      <div className="fixed bottom-20 left-0 right-0 p-4 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700" data-id="element-171">
        <Button variant="primary" fullWidth size="lg" onClick={handleNext} data-id="element-172">
          {currentStep < 4 ? 'Continue' : 'Submit Application'}
        </Button>
      </div>
    </div>;
};
export default ApplicationFlow;